/**
 * SYS-001 — Splash / auto-login screen
 * 
 * Fully automatic screen that:
 * 1. Waits for AuthContext to be ready
 * 2. Checks onboarding state
 * 3. Routes to appropriate screen
 * 
 * No user interaction - process is automatic
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useAuthContext } from '@/contexts/AuthContext';
import { storageService, eventsService } from '@/lib/services';
import splashBackground from '@/assets/splash.webp';

// Route mappings for onboarding steps
const ONBOARDING_ROUTES: Record<number, string> = {
  1: '/welcome', // ONB-101
  2: '/welcome', // ONB-102
  3: '/welcome', // ONB-103
  4: '/welcome', // ONB-104
};

const MIN_SPLASH_DURATION = 400;

export default function Splash() {
  const navigate = useSafeNavigate();
  const { authReady, session } = useAuthContext();
  
  const [minTimePassed, setMinTimePassed] = useState(false);
  const hasNavigated = useRef(false);
  const hasEmittedLaunch = useRef(false);

  // Minimum splash display time
  useEffect(() => {
    const timer = setTimeout(() => setMinTimePassed(true), MIN_SPLASH_DURATION);
    return () => clearTimeout(timer);
  }, []);

  // Emit app launch event once on mount
  useEffect(() => {
    if (hasEmittedLaunch.current) return;
    hasEmittedLaunch.current = true;

    const isFirstLaunch = storageService.isFirstLaunch();
    const hasSessionCookie = storageService.hasSessionCookie();
    const launchIndex = storageService.incrementLaunchIndex();

    eventsService.emitAppLaunch({
      has_session_cookie: hasSessionCookie,
      launch_index: launchIndex,
      is_first_launch: isFirstLaunch,
    });

    eventsService.emitSessionRestoreAttempt(hasSessionCookie);
  }, []);

  /**
   * Determine navigation target based on onboarding and session state
   * Logic follows SYS-001.md exactly
   */
  const navigateBasedOnState = useCallback((
    onboardingState: { completed: boolean; step: number },
    sessionValid: boolean
  ) => {
    const { completed: onboardingCompleted, step: onboardingStep } = onboardingState;

    // Case 1: Onboarding not completed AND step is 1-4
    if (!onboardingCompleted && onboardingStep >= 1 && onboardingStep <= 4) {
      const route = ONBOARDING_ROUTES[onboardingStep] || '/welcome';
      navigate(route, { replace: true });
      return;
    }

    // Case 2: Onboarding completed (or step >= 5)
    if (onboardingCompleted || onboardingStep >= 5) {
      if (sessionValid) {
        // Valid session -> ROOT-001 (catalog)
        navigate('/catalog', { replace: true });
      } else {
        // No valid session -> ONB-101
        navigate('/welcome', { replace: true });
      }
      return;
    }

    // Case 3: Fresh start (step = 0 or undefined) -> ONB-101
    navigate('/welcome', { replace: true });
  }, [navigate]);

  // Navigate when both authReady and minTimePassed
  useEffect(() => {
    if (!authReady || !minTimePassed || hasNavigated.current) return;
    hasNavigated.current = true;

    const sessionValid = !!session;

    // Set userId BEFORE reading onboarding state (fixes race condition)
    if (session?.user?.id) {
      storageService.setCurrentUserId(session.user.id);
    }

    // Get onboarding state (now with correct userId)
    const onboardingState = storageService.getOnboardingState();

    // Emit session restore events
    if (sessionValid) {
      eventsService.emitSessionRestoreSuccess();
    } else {
      eventsService.emitSessionRestoreFailed('No session');
    }

    // Navigate based on state
    navigateBasedOnState(onboardingState, sessionValid);
  }, [authReady, minTimePassed, session, navigateBasedOnState]);

  return (
    <main className="fixed inset-0 flex items-center justify-center overflow-hidden bg-bg-primary">
      {/* Fallback background */}
      <div className="absolute inset-0 bg-bg-accent-pink-page" />

      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${splashBackground})` }}
      />

      {/* Loading indicator - show while waiting */}
      {(!authReady || !minTimePassed) && (
        <div className="relative z-10">
          <div className="w-6 h-6 border-2 border-border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </main>
  );
}
